import { currentProjectSlug, currentBranch } from '../utils/projectPath';

// ── Session refresh (2026-09-14, the accounts login server) ──────────────────
// The access token is short-lived by design; the REFRESH token is an HttpOnly
// cookie the browser sends to /api/auth/refresh by itself (credentials:
// 'include'). On the first 401 of a data call we refresh ONCE — a single
// in-flight refresh shared by every concurrent caller — store the new token
// and retry the call. When the refresh itself fails the session is over:
// storage is cleared and `cortex:session-expired` tells AuthContext to show
// the login page. The sign-in routes themselves never trigger a refresh (a
// wrong password is a 401 too) — but `auth/me` DOES: it is the first call of
// every page load (AuthContext.checkAuthStatus), so an expired access token
// with a live cookie must renew there or the person is signed out on reload.
let refreshInFlight = null;
const AUTH_ENDPOINT_RE = /^auth\//;                                   // carries the refresh cookie
const NO_REFRESH_RE = /^auth\/(login|signup|refresh|logout|sso)(\/|\?|$)/; // a 401 here is final

export const refreshSession = async (backendUrl) => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const r = await fetch(`${backendUrl}api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!r.ok) return null;
      const data = await r.json().catch(() => null);
      if (!data?.access_token) return null;
      localStorage.setItem('access_token', data.access_token);
      if (data.user) {
        try {
          const prev = JSON.parse(localStorage.getItem('user_data') || '{}');
          localStorage.setItem('user_data', JSON.stringify({ ...prev, ...data.user }));
        } catch { localStorage.setItem('user_data', JSON.stringify(data.user)); }
      }
      return data.access_token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

const endSession = () => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
  } catch {}
  try { window.dispatchEvent(new CustomEvent('cortex:session-expired')); } catch {}
};

const fetchData = async (endpoint, options = {}, _retried = false) => {
  // PORTAL (standalone deployment): all /api traffic stays same-origin — the
  // portal nginx routes it to the IN-APP portal-api. The baked absolute
  // cortex URL must never be used there (full runtime isolation).
  const backendUrl = (typeof window !== 'undefined' && window.__PORTAL__?.portal)
    ? '/'
    : import.meta.env.VITE_BACKEND_ADDRESS;
  if (!backendUrl) {
    throw new Error('VITE_BACKEND_ADDRESS environment variable is not defined');
  }

  try {
    // Extract custom options
    const { orderBy, limit, offset, responseType, ...fetchOptions } = options;

    // Get auth token from localStorage. A share visitor signs in on the share
    // page itself and gets an ordinary cortex session — there is no second
    // token type to choose between.
    const token = localStorage.getItem('access_token');

    // Add Authorization header if token exists
    const headers = {
      ...fetchOptions.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // The URL is the single source of truth for the active project + branch.
    // Derive them from the current path and send per-request; the backend routes
    // the Dolt DB/branch from these headers (NOT from the JWT). If there is no
    // project in the URL yet (pre-redirect), we send nothing and the backend
    // falls back to its default.
    const proj = currentProjectSlug();
    if (proj && !headers['X-Cortex-Project']) headers['X-Cortex-Project'] = proj;
    if (proj && !headers['X-Cortex-Branch']) headers['X-Cortex-Branch'] = currentBranch();

    // Acting-org scope (platform admins only — the backend ignores it for
    // everyone else): the org switcher on /projects stores the selection here;
    // sending it on every call keeps all admin views consistently scoped.
    const actingOrg = localStorage.getItem('cortex_acting_org');
    if (actingOrg && !headers['X-Cortex-Org']) headers['X-Cortex-Org'] = actingOrg;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (orderBy) {
      queryParams.append('sort', orderBy);
    }
    if (limit) {
      queryParams.append('limit', limit);
    }
    if (offset !== undefined && offset !== null) {
      queryParams.append('offset', offset);
    }

    // Build URL with query parameters
    const queryString = queryParams.toString();
    const url = `${backendUrl}api/${endpoint}${queryString ? '?' + queryString : ''}`;

    // Use the backend service name and port. Auth routes carry the refresh
    // cookie (login sets it, refresh/logout read it); everything else is
    // bearer-only and sends no cookie.
    const isAuthRoute = AUTH_ENDPOINT_RE.test(endpoint);
    const response = await fetch(url, {
      ...fetchOptions,
      ...(isAuthRoute && fetchOptions.credentials === undefined ? { credentials: 'include' } : {}),
      headers
    });

    // Expired access token on a data call: refresh once and retry; when the
    // refresh fails, the session is over.
    if (response.status === 401 && token && !NO_REFRESH_RE.test(endpoint) && !_retried) {
      const fresh = await refreshSession(backendUrl);
      if (fresh) return fetchData(endpoint, options, true);
      endSession();
    }

    if (!response.ok) {
      // The parsed body IS the error (callers read .error/.message). Attach the
      // HTTP status too: distinguishing 401 (no session) from 403 (signed in,
      // not permitted) drives real behavior — the share page shows its own
      // sign-in gate for the former and "not shared with you" for the latter —
      // and body wording is too fragile to carry that decision. Non-enumerable
      // so it never lands in a JSON.stringify of the error.
      const error = await response.json().catch(() => ({
        error: response.statusText || 'Request failed',
        message: `HTTP ${response.status}`,
      }));
      if (error && typeof error === 'object') {
        Object.defineProperty(error, 'status', { value: response.status, enumerable: false });
      }
      throw error;
    }

    // Handle different response types
    if (responseType === 'arrayBuffer') {
      return await response.arrayBuffer();
    } else if (responseType === 'blob') {
      return await response.blob();
    } else if (responseType === 'text') {
      return await response.text();
    } else {
      // Default to JSON
      return await response.json();
    }
  } catch (error) {
    // Requests aborted by navigating away mid-fetch are expected; logging them
    // at error level makes every page transition look broken in the console.
    // Real network failures ("Failed to fetch" while the page stays up) still
    // matter — downgrade rather than drop, and keep rethrowing either way so
    // callers surface the failure in the UI.
    if (error?.name === "AbortError" || /Failed to fetch/i.test(String(error?.message || ""))) {
      console.warn("fetch interrupted:", endpoint, error?.message || error);
    } else {
      console.error("Error fetching data:", error);
    }
    throw error;
  }
};

export { fetchData };
